import React, { useEffect, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { hasReturnedFromLogout } from '@/auth/oidcConfig';
import { hasLogoutStarted } from '@/auth/session';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { POST_LOGOUT_REDIRECT_URL } from '@/config/env';
import { ROUTE_ROOT } from '@/router/routes';

interface Props {
  children: ReactNode;
}

export const AuthGate: React.FC<Props> = ({ children }) => {
  const { isLoading, isAuthenticated, activeNavigator, error, signinRedirect } =
    useAuth();

  const isReturningFromLogout = hasReturnedFromLogout();
  const isLoggingOut = hasLogoutStarted() || isReturningFromLogout;
  const shouldRedirectToLogin =
    !isLoading &&
    !isAuthenticated &&
    !activeNavigator &&
    !error &&
    !isLoggingOut;

  useEffect(() => {
    if (isReturningFromLogout) {
      window.location.replace(POST_LOGOUT_REDIRECT_URL || ROUTE_ROOT);
    }
  }, [isReturningFromLogout]);

  useEffect(() => {
    if (shouldRedirectToLogin) {
      signinRedirect();
    }
  }, [shouldRedirectToLogin, signinRedirect]);

  if (isLoading || isReturningFromLogout) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <PageSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center text-destructive">
        {error.message}
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};
